import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { createSupplierPayment } from '@/api';
import { toast } from 'sonner';
import type { SupplierInvoice } from '@/types/supplier';
import { format } from 'date-fns';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

interface PayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: SupplierInvoice | null;
  onCompleted?: () => void;
}

export function PaymentDialog({ open, onOpenChange, invoice, onCompleted }: PayDialogProps) {
  const currencySymbol = useCurrencySymbol();
  const remaining = invoice ? invoice.amount - invoice.paidAmount : 0;

  const paymentSchema = z.object({
    amount: z.number()
      .positive('Amount must be greater than 0')
      .max(remaining, `Amount cannot exceed remaining balance of ${currencySymbol}${remaining.toFixed(2)}`),
    date: z.string().min(1, 'Date is required'),
    paymentMethod: z.enum(['cash', 'bank_transfer'], {
      errorMap: () => ({ message: 'Please select a payment method' })
    }),
    notes: z.string().optional()
  });

  type PayForm = z.infer<typeof paymentSchema>;

  const form = useForm<PayForm>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      amount: remaining,
      date: format(new Date(), 'yyyy-MM-dd'),
      paymentMethod: 'cash' as const,
      notes: ''
    }
  });

  useEffect(() => {
    form.reset({ 
      amount: remaining, 
      date: format(new Date(), 'yyyy-MM-dd'), 
      paymentMethod: 'cash' as const,
      notes: '' 
    });
  }, [invoice, form]);

  const handle = async (data: PayForm) => {
    if (!invoice) return;
    try {
      await createSupplierPayment({
        invoiceId: invoice.id,
        amount: data.amount,
        date: data.date,
        paymentMethod: data.paymentMethod,
        notes: data.notes
      });
      toast.success('Payment recorded');
      onOpenChange(false);
      onCompleted && onCompleted();
    } catch (e: any) {
      toast.error('Payment failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handle)} className="space-y-4">
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount (Remaining: {currencySymbol}{remaining.toFixed(2)})</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="paymentMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Method</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional notes" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit">Submit Payment</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}